/**
 * Search Page Enhancements
 * Adds recent searches, search suggestions, and improved UX
 */

class SearchEnhancements {
  constructor() {
    this.recentSearches = this.getRecentSearches();
    this.maxRecentSearches = 5;

    // Find all search inputs - both header and page search
    this.searchInputs = document.querySelectorAll('.vevor-search-input, input[name="q"]');
    this.suggestionsContainer = document.getElementById('search-suggestions');
    this.sortSelect = document.getElementById('SearchSortBy');



    this.init();
  }

  init() {
    if (this.searchInputs && this.searchInputs.length > 0) {
      this.setupSearchTracking();
      this.addRecentSearches();
    }

    if (this.sortSelect) {
      this.setupSortingFunctionality();
    }
  }

  setupSearchTracking() {
    // Track search submissions for all search inputs
    this.searchInputs.forEach((searchInput) => {
      const searchForm = searchInput.closest('form');
      if (searchForm) {
        searchForm.addEventListener('submit', (e) => {
          const query = searchInput.value.trim();
          if (query) {
            this.addToRecentSearches(query);
          }
        });
      }
    });

    // Set up the main event listeners for clearing
    this.setupSearchInputEventListeners();
  }

  setupSearchInputEventListeners() {
    // Handle search input clearing and typing for all search inputs
    if (this.searchInputs.length > 0) {
      this.searchInputs.forEach((searchInput, index) => {

        // Test click handler to verify events work
        searchInput.addEventListener('click', () => {
          console.log(`Click event works for input ${index + 1}!`); // Debug log
        });

        // Handle input events with proper context binding
        const self = this;
        const handleInputChange = (eventType) => {
          return (e) => {
            const query = e.target.value.trim();
            if (query === '' || query.length === 0) {
              self.showEmptyState();
            } else {
              self.hideEmptyState();
            }
          };
        };

        // Set up event listeners for various clearing methods
        searchInput.addEventListener('input', handleInputChange('input'));
        searchInput.addEventListener('search', handleInputChange('search'));
        searchInput.addEventListener('change', handleInputChange('change'));
        searchInput.addEventListener('keyup', handleInputChange('keyup'));

        // Handle blur event for additional clearing detection
        searchInput.addEventListener('blur', () => {
          const query = searchInput.value.trim();
          if (query === '') {
            self.showEmptyState();
          }
        });
      });

      // Set up polling to catch any missed clear events
      this.setupSearchPolling();

      // Set up mutation observer to watch for value changes
      this.setupMutationObserver();
    }

    // Track suggestion clicks
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('vevor-search-suggestion')) {
        const query = e.target.textContent.trim();
        this.addToRecentSearches(query);
      }
    });
  }

  setupSortingFunctionality() {
    this.sortSelect.addEventListener('change', (e) => {
      const sortValue = e.target.value;
      const currentUrl = new URL(window.location);

      // Update the sort_by parameter
      if (sortValue) {
        currentUrl.searchParams.set('sort_by', sortValue);
      } else {
        currentUrl.searchParams.delete('sort_by');
      }

      // Redirect to the new URL
      window.location.href = currentUrl.toString();
    });
  }

  setupSearchPolling() {
    // Keep track of the last known values for all inputs
    const lastValues = Array.from(this.searchInputs).map(input => input.value);

    // Poll every 100ms to catch any missed clear events
    setInterval(() => {
      this.searchInputs.forEach((searchInput, index) => {
        const currentValue = searchInput.value.trim();

        // If value changed from something to empty, trigger empty state
        if (lastValues[index] !== '' && currentValue === '') {
          this.showEmptyState();
        }

        lastValues[index] = currentValue;
      });
    }, 100);
  }

  setupMutationObserver() {
    // Watch for attribute changes on all input elements
    this.searchInputs.forEach((searchInput) => {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
            const query = searchInput.value.trim();
            if (query === '') {
              this.showEmptyState();
            }
          }
        });
      });

      // Start observing this input
      observer.observe(searchInput, {
        attributes: true,
        attributeFilter: ['value']
      });
    });
  }

  showEmptyState() {
    // Redirect to search page without query parameters
    // This will show the natural empty state that's already built into the template
    const currentUrl = new URL(window.location);

    if (currentUrl.searchParams.has('q')) {
      currentUrl.searchParams.delete('q');
      window.location.href = currentUrl.toString();
    }
  }



  hideEmptyState() {
    // With redirect approach, this method is not needed
    // The page will reload with search results when user types
  }

  addRecentSearches() {
    if (!this.suggestionsContainer || this.recentSearches.length === 0) {
      return;
    }

    // Create recent searches section
    const recentSection = document.createElement('div');
    recentSection.className = 'vevor-search-suggestions vevor-search-suggestions--recent';
    recentSection.innerHTML = `
      <h3 class="vevor-search-suggestions__title">Recent Searches</h3>
      <div class="vevor-search-suggestions__list">
        ${this.recentSearches.map(search => 
          `<a href="${window.routes?.search_url || '/search'}?q=${encodeURIComponent(search)}" class="vevor-search-suggestion vevor-search-suggestion--recent">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M12 1v6m0 6v6m11-7h-6m-6 0H1"/>
            </svg>
            ${this.escapeHtml(search)}
            <button type="button" class="vevor-search-suggestion__remove" data-search="${this.escapeHtml(search)}" aria-label="Remove from recent searches">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </a>`
        ).join('')}
      </div>
    `;

    // Insert before popular searches
    this.suggestionsContainer.parentNode.insertBefore(recentSection, this.suggestionsContainer);

    // Add remove functionality
    recentSection.addEventListener('click', (e) => {
      if (e.target.closest('.vevor-search-suggestion__remove')) {
        e.preventDefault();
        e.stopPropagation();
        const button = e.target.closest('.vevor-search-suggestion__remove');
        const searchTerm = button.dataset.search;
        this.removeFromRecentSearches(searchTerm);
        button.closest('.vevor-search-suggestion').remove();
        
        // If no recent searches left, remove the section
        if (recentSection.querySelectorAll('.vevor-search-suggestion').length === 0) {
          recentSection.remove();
        }
      }
    });
  }

  addToRecentSearches(query) {
    if (!query || query.length < 2) return;

    // Remove if already exists
    this.recentSearches = this.recentSearches.filter(search => 
      search.toLowerCase() !== query.toLowerCase()
    );

    // Add to beginning
    this.recentSearches.unshift(query);

    // Limit to max recent searches
    if (this.recentSearches.length > this.maxRecentSearches) {
      this.recentSearches = this.recentSearches.slice(0, this.maxRecentSearches);
    }

    // Save to localStorage
    this.saveRecentSearches();
  }

  removeFromRecentSearches(query) {
    this.recentSearches = this.recentSearches.filter(search => 
      search.toLowerCase() !== query.toLowerCase()
    );
    this.saveRecentSearches();
  }

  getRecentSearches() {
    try {
      const stored = localStorage.getItem('vevor_recent_searches');
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.warn('Failed to load recent searches:', e);
      return [];
    }
  }

  saveRecentSearches() {
    try {
      localStorage.setItem('vevor_recent_searches', JSON.stringify(this.recentSearches));
    } catch (e) {
      console.warn('Failed to save recent searches:', e);
    }
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Auto-initialize when DOM is loaded
function initializeSearchEnhancements() {
  // Wait a bit for the DOM to be fully ready
  setTimeout(() => {
    if (!window.searchEnhancementsInitialized) {
      window.searchEnhancementsInitialized = true;
      new SearchEnhancements();
    }
  }, 100);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeSearchEnhancements);
} else {
  initializeSearchEnhancements();
}

// Also try on window load as a fallback
window.addEventListener('load', () => {
  if (!window.searchEnhancementsInitialized) {
    initializeSearchEnhancements();
  }
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SearchEnhancements;
}
